import { useState, useEffect, useRef } from "react";
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query, 
  orderBy 
} from "firebase/firestore";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  onAuthStateChanged, 
  signOut,
  setPersistence,
  browserSessionPersistence
} from "firebase/auth";
import { app } from "../firebase/firebase";
import styles from "../styles/AdminDashboard.module.scss";

const AdminDashboard = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [gdprFilter, setGdprFilter] = useState("all");
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authChecking, setAuthChecking] = useState(true);

  const db = getFirestore(app, 'student-db');
  const auth = getAuth(app);
  const logoutTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Auto-logout settings (2 hours = 7200000 milliseconds)
  const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours

  // Set persistence to SESSION (logs out when browser closes or after inactivity)
  useEffect(() => {
    setPersistence(auth, browserSessionPersistence)
      .catch((error) => {
        console.error("Error setting persistence:", error);
      });
  }, [auth]);

  // Reset activity timer
  const resetActivityTimer = () => {
    lastActivityRef.current = Date.now();
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }
    
    // Set new logout timer
    logoutTimerRef.current = setTimeout(() => {
      handleAutoLogout();
    }, INACTIVITY_TIMEOUT);
  };

  // Handle auto-logout
  const handleAutoLogout = async () => {
    try {
      await signOut(auth);
      setError("Sessione scaduta per inattività. Effettua nuovamente l'accesso.");
    } catch (err) {
      console.error("Auto-logout error:", err);
    }
  };

  // Activity tracking
  useEffect(() => {
    if (!user) return;

    const handleActivity = () => {
      resetActivityTimer();
    };

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the timer
    resetActivityTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
    };
  }, [user]);

  // Export to CSV function
  const exportToCSV = () => {
    // Create CSV header
    const headers = ['Name', 'Email', 'GDPR Consent', 'Registration Date'];
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...allUsers.map(user => [
        `"${user.name.replace(/"/g, '""')}"`, // Escape quotes in names
        `"${user.email}"`,
        user.gdprConsentAccepted ? 'Accepted' : 'Pending',
        formatDate(user.timestamp)
      ].join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `metodoalps-subscribers-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthChecking(false);
      if (user) {
        fetchUsers();
      } else {
        setAllUsers([]);
        setFilteredUsers([]);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    filterUsers();
  }, [allUsers, searchTerm, gdprFilter]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError("Login failed: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      setError("Logout failed: " + err.message);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError("");

      const baseRef = collection(db, 'subscribers');
      let snapshot;

      try {
        const q = query(baseRef, orderBy('createdAt', 'desc'));
        snapshot = await getDocs(q);
      } catch (orderErr) {
        console.error('Order by createdAt failed, falling back to unsorted:', orderErr);
        snapshot = await getDocs(baseRef);
        setError('Ordine per data non disponibile; dati mostrati senza ordinamento.');
      }
      
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : null;
        return {
          id: doc.id,
          name: data.name || 'N/A',
          email: data.email || 'N/A',
          gdprConsentAccepted: data.gdprConsentAccepted || false,
          timestamp: createdAt || new Date()
        };
      });

      setAllUsers(users);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch user data. Please check your Firebase configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = allUsers.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          user.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      let matchesGdpr = true;
      if (gdprFilter === 'accepted') {
        matchesGdpr = user.gdprConsentAccepted === true;
      } else if (gdprFilter === 'pending') {
        matchesGdpr = user.gdprConsentAccepted === false;
      }

      return matchesSearch && matchesGdpr;
    });

    setFilteredUsers(filtered);
  };

  const updateStats = () => {
    const totalUsers = allUsers.length;
    const gdprAccepted = allUsers.filter(user => user.gdprConsentAccepted).length;
    const gdprPending = totalUsers - gdprAccepted;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayUsers = allUsers.filter(user => {
      const userDate = new Date(user.timestamp);
      userDate.setHours(0, 0, 0, 0);
      return userDate.getTime() === today.getTime();
    }).length;

    return { totalUsers, gdprAccepted, gdprPending, todayUsers };
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const stats = updateStats();

  if (authChecking) {
    return (
      <div className={styles.adminDashboard}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>MetodoALPS Admin</h1>
            <p>Checking authentication status...</p>
          </div>
          <div className={styles.loginForm}>
            <p>Please wait while we check your authentication status.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles.adminDashboard}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>MetodoALPS Admin</h1>
            <p>User Registration Management</p>
          </div>
          
          <div className={styles.loginForm}>
            <form onSubmit={handleLogin}>
              <input
                type="email"
                placeholder="Admin Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Admin Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="submit" disabled={isLoading}>
                {isLoading ? "Logging in..." : "Login"}
              </button>
            </form>
            {error && <div className={styles.error}>{error}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminDashboard}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>MetodoALPS Dashboard</h1>
          <p>User Registration Management</p>
          <div className={styles.userInfo}>
            <span>Logged in as: {user.email}</span>
            <button onClick={handleLogout} className={styles.logoutBtn}>
              Logout
            </button>
          </div>
        </div>

        <div className={styles.stats}>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{stats.totalUsers}</div>
            <div className={styles.statLabel}>Total Users</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{stats.gdprAccepted}</div>
            <div className={styles.statLabel}>GDPR Accepted</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{stats.gdprPending}</div>
            <div className={styles.statLabel}>GDPR Pending</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{stats.todayUsers}</div>
            <div className={styles.statLabel}>Today's Registrations</div>
          </div>
        </div>

        <div className={styles.controls}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            <select
              value={gdprFilter}
              onChange={(e) => setGdprFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">All Users</option>
              <option value="accepted">GDPR Accepted</option>
              <option value="pending">GDPR Pending</option>
            </select>
            <button onClick={fetchUsers} className={styles.refreshBtn}>
              Refresh Data
            </button>
            <button onClick={exportToCSV} className={styles.exportBtn}>
              Export CSV
            </button>
          </div>
        </div>

        <div className={styles.tableContainer}>
          {isLoading && <div className={styles.loading}>Loading user data...</div>}
          {error && <div className={styles.error}>{error}</div>}
          
          {!isLoading && filteredUsers.length === 0 && (
            <div className={styles.noData}>No users found</div>
          )}
          
          {!isLoading && filteredUsers.length > 0 && (
            <table className={styles.usersTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>GDPR Consent</th>
                  <th>Registration Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${
                        user.gdprConsentAccepted ? styles.statusAccepted : styles.statusPending
                      }`}>
                        {user.gdprConsentAccepted ? 'Accepted' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.timestamp}>
                        {formatDate(user.timestamp)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 